import * as React from "react";
import cn from "classnames";
import ReactModal from "react-modal";
import { useMessageFormatter } from "@brite-inc/ui-hooks/dist/i18n";
import { Icon } from "../Icon";
import { locales } from "../i18n";
import { LoadingBar } from "../LoadingBar";
import { Button } from "../Button";
import styles from "./styles.module.scss";

interface IProps {
    title?: string;
    isOpen: boolean;
    withCloseButton?: boolean;
    withLoadingBar?: boolean;
    fixedHeight?: boolean;
    onClose: () => void;
    children?: React.ReactNode;
    className?: string;
    contentClassName?: string;
    overlayClassName?: string;
    setOverlayEl?: (contentEl: HTMLElement) => void;
    testId?: string;
    headerClassName?: string;
}

export function Modal(
    {
        title,
        isOpen,
        onClose,
        className,
        contentClassName,
        overlayClassName,
        children,
        withCloseButton,
        withLoadingBar,
        fixedHeight,
        setOverlayEl,
        testId,
        headerClassName,
    }: IProps = {
        title: undefined,
        isOpen: false,
        onClose: () => {},
        withCloseButton: true,
        withLoadingBar: false,
        fixedHeight: false,
        setOverlayEl: () => {},
    },
): JSX.Element {
    const formatMessage = useMessageFormatter(locales.default);

    const showCloseButton = withCloseButton ?? true;
    const showLoadingBar = withLoadingBar ?? false;

    return (
        <ReactModal
            ariaHideApp={false}
            className={cn([
                styles.modal,
                className && className,
                fixedHeight && styles.modalFixedHeight,
            ])}
            isOpen={isOpen}
            onRequestClose={onClose}
            overlayClassName={cn([
                styles.overlay,
                overlayClassName && overlayClassName,
            ])}
			// @ts-ignore
            overlayRef={(overlayEl) => setOverlayEl && setOverlayEl(overlayEl)}
        >
            <div data-testid={testId}>
                <div className={cn([headerClassName && headerClassName, styles.header])}>
                    {title ? <h4 className={styles.title}>{title}</h4> : null}

                    {showCloseButton ? (
                        <Button
                            className={styles.closeButton}
                            onClick={onClose}
                            variant="text"
                            title={formatMessage("modal.closeModal")}
                            aria-label={formatMessage("modal.closeModal")}
                        >
                            <Icon name="times" className={styles.closeIcon} />
                        </Button>
                    ) : null}
                </div>

                <div
                    className={cn([
                        contentClassName && contentClassName,
                        styles.content,
                    ])}
                >
                    {children}
                </div>

                {showLoadingBar ? (
                    <div className={styles.loadingBar}>
                        <LoadingBar isVisible={true} />
                    </div>
                ) : null}
            </div>
        </ReactModal>
    );
}
